import api from './api';

export interface CalendarEvent {
  _id?: string;
  id?: string;
  sourceId?: string | null;
  externalId?: string | null;
  title?: string | null;
  description?: string | null;
  startDate?: number | null;
  endDate?: number | null;
  isMultiDay?: boolean;
  dateFormatted?: string | null;
  venue?: string | null;
  city?: string | null;
  country?: string | null;
  countryCode?: string | null;
  categoryId?: string | null;
  categoryIds?: string[];
  tags?: string[];
  organization?: string | null;
  prizeMoney?: number | null;
  prizeMoneyFormatted?: string | null;
  prizeMoneyUnit?: string | null;
  sourceUrl?: string | null;
  registrationUrl?: string | null;
  ticketUrl?: string | null;
  image?: string | null;
  status?: 'scheduled' | 'ongoing' | 'completed' | 'cancelled' | string | null;
  isFeatured?: boolean;
  createdAt?: number | null;
  updatedAt?: number | null;
}

export interface CalendarSource {
  _id?: string;
  id?: string;
  name?: string | null;
  shortName?: string | null;
  organization?: string | null;
  description?: string | null;
  image?: string | null;
  active?: boolean;
  sortOrder?: number | null;
  eventCount?: number | null;
}

export interface CalendarCategory {
  _id?: string;
  id?: string;
  name?: string | null;
  slug?: string | null;
  color?: string | null;
  icon?: string | null;
  sortOrder?: number | null;
  active?: boolean;
}

class CalendarService {
  async getUpcomingEvents(limit: number = 500, offset: number = 0): Promise<CalendarEvent[]> {
    const res = await api.get('/api/calendar/events', { params: { limit, offset } });
    return Array.isArray(res.data) ? res.data : [];
  }

  async getEventsByDateRange(
    start: number,
    end: number,
    organization?: string | null,
    categoryId?: string | null,
  ): Promise<CalendarEvent[]> {
    const params: Record<string, string | number> = { start, end };
    if (organization) params.organization = organization;
    if (categoryId) params.categoryId = categoryId;
    const res = await api.get('/api/calendar/events/range', { params });
    return Array.isArray(res.data) ? res.data : [];
  }

  async searchEvents(query: string, limit: number = 50): Promise<CalendarEvent[]> {
    if (!query || query.length < 2) return [];
    const res = await api.get('/api/calendar/events/search', { params: { query, limit } });
    return Array.isArray(res.data) ? res.data : [];
  }

  async getAllSources(): Promise<CalendarSource[]> {
    const res = await api.get('/api/calendar/sources');
    return Array.isArray(res.data) ? res.data : [];
  }

  async getAllCategories(): Promise<CalendarCategory[]> {
    const res = await api.get('/api/calendar/categories');
    return Array.isArray(res.data) ? res.data : [];
  }

  formatLocation(event: CalendarEvent): string {
    const parts: string[] = [];
    if (event.venue) parts.push(event.venue);
    if (event.city) parts.push(event.city);
    if (event.country) parts.push(event.country);
    return parts.join(', ') || '-';
  }

  formatDateRange(event: CalendarEvent): string {
    if (event.dateFormatted && event.dateFormatted.trim().length > 0) {
      return event.dateFormatted;
    }
    if (!event.startDate) return '-';
    const start = new Date(event.startDate);
    const startStr = start.toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
    if (!event.endDate || event.endDate === event.startDate) return startStr;
    const end = new Date(event.endDate);
    const endStr = end.toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
    return `${startStr} - ${endStr}`;
  }

  buildFileUrl(fileId?: string | null): string | null {
    if (!fileId) return null;
    if (fileId.startsWith('http://') || fileId.startsWith('https://')) return fileId;
    const base = api.defaults.baseURL || '';
    return `${base}/api/file/${fileId}`;
  }
}

export default new CalendarService();
